import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PodiumRewardsCard } from "./PodiumBadges";
import CashGameMetaBar from "./CashGameMetaBar";
import HostedByQushi from "./HostedByQushi";
import LeaguePlayTopBar from "./LeaguePlayTopBar";
import CashPrizeHero, { cashEntryState } from "./CashPrizeCard";
import { descriptionToText } from "./leagueStatus";
import {
  assetSelectionLabel,
  assetScopeNote,
  endMomentLabel,
  leagueDateLabel,
  startMomentLabel,
  portfolioVisibilityGameShort,
  entryRuleLabel,
} from "./leagueFormat";
import { standaloneLeaderboardPath } from "../../../utils/leaguePath";
import useStickyRail from "../../../utils/useStickyRail";
import { openLegalLink } from "../../../utils/openLegalLink";
import {
  AnalyticsIcon,
  CalendarCheckIcon,
  CashIcon,
  ChevronRightIcon,
  DollarCircleIcon,
  SmallCalendarIcon,
  SmallLockIcon,
  NoEntryIcon,
  SwapArrowsIcon,
  TrendUpIcon,
  UpDownArrowsIcon,
  TrophyOutlineIcon,
  TrophyIcon,
} from "./icons";

// The Cash Game landing page.
//
// A cash game used to borrow the PUBLIC league landing page, which pitches a
// league someone else is running and argues for joining it. A cash game is a
// different proposition — QUSHi runs it, entry is free, and what the reader is
// deciding about is a real cash prize and the rules attached to it — so the
// client designed it its own page (Stitch 4746 mobile / 4747 desktop) and this
// is that page.
//
// Layout, per the two design files:
//
//   MOBILE          banner · benefits · prize · assets · rules · PODIUM ·
//                   entrants, with the CTA docked to the bottom of the screen
//   DESKTOP         banner · benefits · [ prize + assets | rules ] ·
//                   PODIUM full width · entrants + CTA full width
//
// Podium Rewards is in neither Stitch file. It is in the client's brief, as a
// separate instruction with its own placement for each breakpoint: "Please copy
// and paste our current Podium rewards section from the public leagues landing
// page... On mobile, place Podium Rewards after Contest Rules and before
// Entrants. On desktop, make it full width beneath the Prize, Eligible Assets,
// and Contest Rules area, then place Entrants beneath it." Hence one instance in
// the mobile stack and one in the desktop grid, each hidden at the other
// breakpoint — the card itself is the shared PodiumRewardsCard, unforked.
//
// The app's own fixed QUSHi header stays: "In the actual build, continue using
// our existing fixed QUSHi header." So the design's back/share/3-dot bar is
// deliberately not built.

// ── static copy ───────────────────────────────────────────────────────────
// Client: "The following Cash Game information can remain static for launch."
// Every string below is from that list or from the design, and none of it is
// derived from the game — so it lives here rather than being threaded through
// props that would imply it varies.
const STARTING_CASH = { value: "$100,000", note: "virtual cash" };
const TRADE_LIMIT = { value: "Unlimited", note: "trades" };
// ALLOWED POSITIONS is in the client's new desktop design as a rule of its own.
// It is STATIC, and that is a fact about the product rather than a shortcut:
// QUSHi supports long and short on every competition (positions carry a
// `direction` of "long" or "short" everywhere in Trade and Portfolio) and there
// is no per-game column that could turn shorting off. So it reads as the
// constant it is, in the same block of static Cash Game copy the client already
// signed off — "The following Cash Game information can remain static for
// launch." FLAGGED to the client: their mobile note asks for "its dynamic
// value", and there is no field to be dynamic about yet.
const ALLOWED_POSITIONS = "Long & short";
// Client Rev4 (0014888 item 2) supplied this wording verbatim: "Highest return
// wins. Lowest max drawdown is the tiebreaker."
const WINNING_CRITERIA = "Highest return wins. Lowest max drawdown is the tiebreaker.";
const ELIGIBILITY_NOTE = "Available to eligible U.S. residents.";

// The header's ground, straight from the client's design — "I like the header
// color in my Stitch design; if you can, match it." Used at BOTH widths now:
// "Match the hero background to the same dark teal used on desktop."
const HEADER_GRADIENT =
  "linear-gradient(135deg, #00383C 0%, #004C50 50%, #002B2E 100%)";

// "header is the cash game pill, title, and tagline" (desktop), and "Header: Add
// the CASH GAME eyebrow above the game title" (mobile) — the same eyebrow, so
// one component and no breakpoint fork. QUSHi green on a tenth of itself, which
// is the design's own treatment and uses the brand green the rest of League Play
// already carries (#35B23C), not a colour of Stitch's.
const CashGamePill = () => (
  <span className="inline-block rounded-full border border-[#35B23C]/30 bg-[#35B23C]/10 px-2.5 py-0.5 font-montserrat text-[10px] font-bold uppercase tracking-wider text-[#35B23C] md:text-[11px]">
    Cash Game
  </span>
);

// `pad` is a prop rather than something callers append to `className`, because
// two `md:p-*` utilities on one element resolve by stylesheet order, not by the
// order they are written — a caller "overriding" md:p-5 with md:p-6 would work
// and md:p-4 would silently not. The desktop design sets its cards at p-6 and
// the rules card at p-8, so those are passed here instead.
// Client: "The container cards should match the exact container cards, shadows,
// etc., we use for league play landing pages. These pages' content can vary, but
// the bones need to be the same."
//
// The league play landing cards are PublicLeagueJoinCard's `Card`:
// `rounded-2xl border border-gray-200 bg-white`, and NO shadow. This page was
// drawing `rounded-xl`, which is the one place it differed. Scoped to `md` and
// up because that instruction arrived in the client's desktop message and the
// phone's landing is a separate pass.
const Card = ({ className = "", pad = "p-4 md:p-5", children }) => (
  <div
    className={`rounded-xl border border-gray-200 bg-white md:rounded-2xl ${pad} ${className}`}
  >
    {children}
  </div>
);

// A boxed glyph, the size the design gives it. One component so a wide glyph and
// a tall one occupy the same footprint — the rule the client set for the public
// league card's four icons, applied here too.
const IconBox = ({ children, className = "" }) => (
  <span
    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-[#F8FAFA] ${className}`}
  >
    {children}
  </span>
);

// One cell of the Contest Rules grid: label in small caps, the value, and — for
// the two cells that carry a unit rather than a fact — a small grey line under
// it ("virtual cash", "trades").
//
// STARTS and ENDS used to add a second BOLD line for the ET time, the same size
// as the date above it, because the time was half of the answer rather than a
// footnote to it. Both halves are one string now: "Contest Rules: … Use a middle
// dot between the date and time, such as `Sep 4 · 8 AM ET`." So there is one
// treatment for the second line again, and it is the quiet one.
//
// The desktop filled box is gone too — see the note on the grid below.
const RuleStat = ({ icon, label, value, note, className = "" }) => (
  <div className={`flex items-start gap-3 ${className}`}>
    <IconBox>{icon}</IconBox>
    <div className="min-w-0">
      <p className="text-[9px] font-bold uppercase tracking-tight text-secondary">
        {label}
      </p>
      <p className="mt-1 text-[13px] font-bold leading-tight text-[#0F172A]">{value}</p>
      {note && (
        <p className="text-[11px] font-medium leading-tight text-secondary">{note}</p>
      )}
    </div>
  </div>
);

// "Sep 24 · 8 AM ET", set so that it breaks in the right place when it cannot
// fit on one line.
//
// Client: "Use a middle dot between the date and time, such as `Sep 4 · 8 AM
// ET`." Measured in the rules grid, the value cell is 71px wide at 320, 91 at
// 360, 99 at 375 and 103 at 390, and the string needs 103 — so it fits on one
// line only on the widest phones, and there is no icon size or gutter that buys
// back 32px at 320.
//
// Left alone it wrapped before the last word and stranded "ET" on a line of its
// own. Each half is `whitespace-nowrap` instead, so the only place it can break
// is the space after the dot: "Sep 24 ·" over "8 AM ET", which is the date over
// the time and reads as one fact rather than a broken string. Wide enough, and
// it is the single line the client asked for.
const MomentValue = ({ value, fallback }) => {
  const parts = String(value || "").split(" \u00b7 ");
  if (parts.length !== 2) return <>{value || fallback}</>;
  return (
    <>
      <span className="whitespace-nowrap">{parts[0]} ·</span>{" "}
      <span className="whitespace-nowrap">{parts[1]}</span>
    </>
  );
};

// A full-width rule that is a sentence rather than a figure: TRADING RULES and
// WINNING CRITERIA both read this way in the design.
const RuleBlock = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 border-t border-divider pt-4">
    <IconBox>{icon}</IconBox>
    <div className="min-w-0 flex-1">
      <p className="text-[9px] font-bold uppercase tracking-tight text-secondary">
        {label}
      </p>
      <div className="mt-1 text-sm font-bold leading-snug text-[#0F172A]">{children}</div>
    </div>
  </div>
);

// The start and end dates in the meta rows. Through leagueDateLabel rather than
// its own toLocaleDateString, so this page spells a date exactly as the league
// cards do — the client asked for one format and no year inside the current
// year ("The year might take up too much space, especially on mobile").
const dateLabel = (value) => leagueDateLabel(value) || "\u2014";

// ── the prize block ───────────────────────────────────────────────────────
// THE TROPHY IS GONE. Client: "Prize cards: Replace the current trophy based
// cards with the new typography led designs. Do not use a trophy or other prize
// imagery."
//
// So `PrizeTrophy` — the client's own /cash-game-trophy.png with the outline
// TrophyIcon behind it — is removed rather than hidden. Nothing else rendered
// it, and leaving a component nobody calls is how a trophy finds its way back
// onto the page later.

// THE PRIZE CARD is CashPrizeCard now — the landing page, the Game details
// preview and the competition home rail all render it. See that file.
//
// MOBILE gains the header row the desktop card already had. Client: "Move Starts
// in 8h (status line) into the top-right corner of the prize card, like the
// desktop version. Put the Entry Open Pill top left just like desktop version."
// So the phone's card is the desktop card's hero, unforked, and the separate
// host-and-countdown band below the metabar goes with it.
const PrizeCard = ({ game }) => (
  <Card pad="p-5">
    <CashPrizeHero game={game} />
  </Card>
);

const EligibleAssetsCard = ({ game }) => (
  <Card className="flex items-center gap-3">
    <IconBox className="h-10 w-10 md:h-12 md:w-12 md:rounded-xl">
      <TrendUpIcon className="h-5 w-5 md:h-6 md:w-6" stroke="#22A36B" />
    </IconBox>
    <div className="min-w-0">
      <h2 className="font-montserrat text-[15px] font-bold text-[#0F172A]">
        Eligible Assets
      </h2>
      {/* The existing asset system, unchanged: the category when the whole
          category is in play, or the client's own count format ("7 coins",
          "7 stocks", "5 pairs") for a custom selection. Same helper, and the
          same mobile/desktop wording split, as every other league screen. */}
      <p className="text-[12px] font-bold text-secondary">
        <span className="md:hidden">{assetSelectionLabel(game, "mobile")}</span>
        <span className="hidden md:inline">{assetSelectionLabel(game, "desktop")}</span>
      </p>
      {/* The design's third line. Derived from the category rather than fixed —
          see assetScopeNote, and note the wording there is OURS pending the
          client's own copy. Dropped entirely for a custom asset list. */}
      {assetScopeNote(game) && (
        <p className="text-[11px] text-tertiary">{assetScopeNote(game)}</p>
      )}
    </div>
  </Card>
);

const ContestRulesCard = ({ game, className = "" }) => {
  // TRADING RULES is the league's Description field: "Connect this field to
  // TRADING RULES on the Cash Game landing page. This content is dynamic and may
  // be different for each Cash Game."
  //
  // The wizard's description is rich text, so it is converted rather than
  // stripped — descriptionToText keeps the organiser's own line breaks, which is
  // a fix the client already asked for on the private league landing page. It is
  // also free-form and can be far longer than the design's single bold line, so
  // it is clamped with a Read more rather than allowed to push the rest of the
  // card off the screen.
  const [expanded, setExpanded] = useState(false);
  const rules = descriptionToText(game?.description);

  return (
    <Card pad="p-4 md:p-8" className={`lg:flex lg:flex-col ${className}`}>
      <h2 className="font-montserrat text-base font-bold text-[#0F172A] md:text-xl">
        Contest Rules
      </h2>

      {/* The grid the client's desktop design draws: eight facts in two columns,
          reading ELIGIBLE ASSETS · STARTS / STARTING CASH · ENDS / TRADE LIMIT ·
          PORTFOLIO VISIBILITY / JOIN AFTER START · ALLOWED POSITIONS.

          Four of those eight are DESKTOP ONLY here, for two different reasons.
          ELIGIBLE ASSETS has its own card on the phone and loses it at desktop,
          where the entry card states the short form and this card states the
          full one. PORTFOLIO VISIBILITY and JOIN AFTER START are already on the
          phone as labelled one-liners below this grid, which is how the phone
          design draws them; the desktop design promotes them into the grid, so
          they appear once at each width and never twice. ALLOWED POSITIONS is
          new, and the client has asked for it on the phone too — that is their
          mobile message and a pass of its own.

          `md:order-*` rather than a second grid. The phone's order is fixed by
          the DOM (STARTS · ENDS / STARTING CASH · TRADE LIMIT, exactly as it
          ships today) and the desktop order is set by `order`, so one set of
          cells serves both and there is no second copy of a value to keep in
          step. */}
      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6 md:gap-x-8">
        <RuleStat
          className="hidden md:flex md:order-1"
          icon={<TrendUpIcon className="h-[18px] w-[18px]" stroke="#22A36B" />}
          label="Eligible assets"
          value={assetSelectionLabel(game, "desktop")}
          note={assetScopeNote(game)}
        />
        {/* Client: "Contest Rules: Keep Starts and Ends in two columns. Use a
            middle dot between the date and time, such as `Sep 4 · 8 AM ET`."

            That IS `startMomentLabel` / `endMomentLabel` — the app's one format
            for a competition's exact moment, already printed by the public and
            private landing pages and the in-league details rail. So the phone
            stops stacking the date over a separate 8 AM ET line and prints the
            same single string the desktop does. No breakpoint fork left here. */}
        <RuleStat
          className="md:order-2"
          icon={<SmallCalendarIcon className="h-[18px] w-[18px]" />}
          label="Starts"
          value={
            <MomentValue
              value={startMomentLabel(game)}
              fallback={dateLabel(game?.start_date)}
            />
          }
        />
        <RuleStat
          className="md:order-4"
          icon={<CalendarCheckIcon className="h-[18px] w-[18px]" />}
          label="Ends"
          value={
            <MomentValue
              value={endMomentLabel(game)}
              fallback={dateLabel(game?.end_date)}
            />
          }
        />
        <RuleStat
          className="md:order-3"
          icon={<DollarCircleIcon className="h-[18px] w-[18px]" />}
          label="Starting cash"
          value={STARTING_CASH.value}
          note={STARTING_CASH.note}
        />
        <RuleStat
          className="md:order-5"
          icon={<SwapArrowsIcon className="h-[18px] w-[18px]" />}
          label="Trade limit"
          value={TRADE_LIMIT.value}
          note={TRADE_LIMIT.note}
        />
        <RuleStat
          className="hidden md:flex md:order-6"
          icon={<SmallLockIcon className="h-[18px] w-[18px]" />}
          label="Portfolio visibility"
          value={portfolioVisibilityGameShort(!!game?.private_portfolio)}
        />
        {/* ENTRY, not JOIN AFTER START. Client: "Contest Rules: Replace
            `JOIN AFTER START: Not allowed` with `ENTRY: Entry closes when the
            game starts.`"

            Applied at BOTH widths although they wrote it under their mobile
            notes, because the alternative is one page calling the same rule two
            different things depending on the window — the drift they keep
            asking us to avoid ("Keep us consistent … stitch is just a visual and
            drifts big time on the foundation"). The desktop design's own
            "Join After Start" is a Stitch string, and this is the client's copy;
            copy wins. FLAGGED to them, since it is a reading of two messages
            rather than a line either one wrote. */}
        <RuleStat
          className="hidden md:flex md:order-7"
          icon={<NoEntryIcon className="h-[18px] w-[18px]" stroke="#22A36B" />}
          label="Entry"
          value={entryRuleLabel(!!game?.join_after_start)}
        />
        <RuleStat
          className="hidden md:flex md:order-8"
          icon={<UpDownArrowsIcon className="h-[18px] w-[18px]" />}
          label="Allowed positions"
          value={ALLOWED_POSITIONS}
        />
      </div>

      {/* The two saved settings, shown as the design shows them — the label in
          small caps and the league's own answer beside it. The wording comes from
          leagueFormat, the same strings every other league screen prints, so a
          cash game cannot describe its own rules differently. For QUSHi's cash
          games these read "Hidden during game" and "Not allowed". */}
      {/* Each of these is ONE line — label, colon, answer — as the design draws
          them. Left to wrap, "PORTFOLIO VISIBILITY:" broke away from its answer
          and "Hidden during game" split across two lines, which turned a
          two-word fact into a four-line paragraph. `flex-nowrap` plus
          `whitespace-nowrap` on both halves is what holds them together; the
          tracking is dropped from `wider` to `wide` on the labels to buy the
          few pixels that costs on a 360px phone. */}
      {/* MOBILE ONLY from here: the desktop grid above states these three, and
          the phone states them as full-width rows — which is how the client
          describes them. "Keep Portfolio Visibility as a full-width row", "Add
          ALLOWED POSITIONS as a full width row with its dynamic value", and the
          ENTRY sentence that replaces JOIN AFTER START.

          Each is ONE line — label, colon, answer. Left to wrap, "PORTFOLIO
          VISIBILITY:" broke away from its answer and turned a two-word fact into
          a four-line paragraph; `flex-nowrap` plus `whitespace-nowrap` on the
          label is what holds them together. ENTRY's answer is a sentence and is
          allowed to wrap, because it is one. */}
      <div className="mt-6 space-y-2 md:hidden">
        <div className="flex flex-nowrap items-center gap-2">
          <SmallLockIcon className="h-4 w-4 shrink-0" stroke="#6B7280" />
          <span className="whitespace-nowrap text-[11px] font-bold uppercase tracking-wide text-secondary">
            Portfolio visibility:
          </span>
          {/* The client's own wording for this row: "PORTFOLIO VISIBILITY:
              Hidden during game". The row already names the subject, so the
              value is the short form, and "game" rather than "league" is the
              same League->Game swap they set for every cash game surface. */}
          <span className="whitespace-nowrap text-[12px] font-semibold text-[#0F172A]">
            {portfolioVisibilityGameShort(!!game?.private_portfolio)}
          </span>
        </div>
        <div className="flex flex-nowrap items-center gap-2">
          <UpDownArrowsIcon className="h-4 w-4 shrink-0" stroke="#6B7280" />
          <span className="whitespace-nowrap text-[11px] font-bold uppercase tracking-wide text-secondary">
            Allowed positions:
          </span>
          <span className="whitespace-nowrap text-[12px] font-semibold text-[#0F172A]">
            {ALLOWED_POSITIONS}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <NoEntryIcon className="mt-0.5 h-4 w-4 shrink-0" stroke="#6B7280" />
          <span className="whitespace-nowrap text-[11px] font-bold uppercase leading-5 tracking-wide text-secondary">
            Entry:
          </span>
          <span className="text-[12px] font-semibold leading-5 text-[#0F172A]">
            {entryRuleLabel(!!game?.join_after_start)}
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {rules && (
          <RuleBlock
            icon={<AnalyticsIcon className="h-[18px] w-[18px]" />}
            label="Trading rules"
          >
            <p
              className={expanded ? "whitespace-pre-line" : "overflow-hidden whitespace-pre-line"}
              style={
                expanded
                  ? undefined
                  : {
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 3,
                    }
              }
            >
              {rules}
            </p>
            {/* Only offered when there is more than the clamp shows. Measured the
                same way the public league card measures it would need a ref per
                render; a word count is enough here because the clamp is three
                lines of a bold 14px string. */}
            {rules.split(/\s+/).length > 24 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-1 text-xs font-semibold text-[#004C50] hover:underline"
              >
                {expanded ? "Read less" : "Read more"}
              </button>
            )}
          </RuleBlock>
        )}

        <RuleBlock
          icon={<TrophyOutlineIcon className="h-[18px] w-[18px]" stroke="#22A36B" />}
          label="Winning criteria"
        >
          <p>{WINNING_CRITERIA}</p>
        </RuleBlock>
      </div>

      {/* Client: "I have attached the Official Rules for the Cash Games landing
          pages and both Game details preview pages, with CTA and without CTA.
          In-app and desktop should open the same way the terms and privacy
          policies do on each platform." — so openOfficialRules goes through the
          same openLegalLink the Terms / Privacy links use. */}
      <div className="mt-6 flex justify-end lg:mt-auto lg:pt-6">
        <button
          type="button"
          onClick={openOfficialRules}
          className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#004C50]"
        >
          View full rules
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </Card>
  );
};

// ── the sticky entry card (DESKTOP) ───────────────────────────────────────
// Client: "The right-side card becomes sticky and stops at the end of the
// contest rules card."
//
// The stopping is structural rather than a measured height: the rail is
// `sticky` and `self-start` inside a flex row whose other column ends with
// Contest Rules, so the row's height IS the left column's height and the card
// can travel no further than its bottom. Nothing to keep in sync when the rules
// card grows a line.
//
// The card carries the prize, which is why the phone's PrizeCard and Eligible
// Assets card are not also drawn at this width — the design moves both facts in
// here, and printing them twice on one screen is what the client means by the
// desktop page being "super weak".
const EntryCard = ({ game, joining, disabled, onJoin, showCta = true }) => {
  const navigate = useNavigate();
  const state = cashEntryState(game);

  const onCta = () => {
    if (state.action === "join") return onJoin && onJoin();
    if (state.action === "leaderboard") {
      return navigate(standaloneLeaderboardPath(game?.vanity_url));
    }
    return navigate(`/contest/${game?.vanity_url}`);
  };

  return (
    <Card pad="p-6">
      <CashPrizeHero game={game} />

      {/* The design's two summary rows. Both values come from the helpers every
          other league screen reads — assetSelectionLabel and endMomentLabel — so
          "Crypto" and "Sep 18 · 8 PM ET" are spelled here exactly as they are in
          Contest Rules beside it. Stitch prints them in green; the app's value
          colour is #0F172A, and the client asked us not to drift on text copy
          colours. */}
      <dl className="my-5 space-y-2 border-y border-divider py-3.5 text-xs">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-secondary">Eligible assets</dt>
          <dd className="truncate font-semibold text-[#0F172A]">
            {assetSelectionLabel(game, "desktop")}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-secondary">Ends</dt>
          <dd className="whitespace-nowrap font-semibold text-[#0F172A]">
            {endMomentLabel(game) || "\u2014"}
          </dd>
        </div>
      </dl>

      {showCta && (
        <>
          <button
            type="button"
            onClick={onCta}
            disabled={state.action === "join" && (disabled || joining)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#004C50] py-3.5 font-montserrat text-sm font-bold tracking-wide text-white transition-colors hover:bg-[#003437] disabled:opacity-60"
          >
            {state.action === "join" && <CashIcon className="h-4 w-4" stroke="#FFFFFF" />}
            {state.action === "join" && joining ? "Entering\u2026" : state.cta}
          </button>
          <p className="mt-2 text-center text-[11px] text-secondary">
            <EligibilityNote />
          </p>
        </>
      )}
    </Card>
  );
};

// ENTRANTS IS GONE. Client: "Entrants: Remove the Entrants card for now. We will
// add it back after participation reaches a useful social proof threshold."
//
// Deleted rather than hidden. A one-entrant game was showing "1 entered" beside
// a single face, which is the opposite of social proof, and the card is in
// neither of the client's new designs. When it returns it returns with a
// threshold attached, which is a different component from this one.

// The Cash Games Official Rules PDF (public/). Absolute, because inside the
// Flutter shell the URL is handed to the native in-app browser, which has no
// page to resolve a relative path against.
//
// A new version of the rules REPLACES this same file, so a link shared earlier
// still opens the current rules. Bump `v` with each replacement: browsers and
// the in-app browser cache PDFs, and without it a returning reader can be shown
// the previous rules. v=2 -- "Account liquidation" added (client, Sep 30).
const OFFICIAL_RULES_URL = `${window.location.origin}${process.env.PUBLIC_URL || ""}/QUSHi_CashGames_Official_Rules.pdf?v=2`;

// Same event name the signup Terms link sends — the native shell only opens the
// in-app browser for names it already handles.
const openOfficialRules = () => openLegalLink("terms_conditions", OFFICIAL_RULES_URL);

// The legal line, in one place. It appears above the docked CTA on a phone,
// under the button in the desktop entry card, and — on a preview, which has no
// button at all — as the last line of the page. Three positions, one sentence.
const EligibilityNote = () => (
  <>
    {ELIGIBILITY_NOTE}{" "}
    <button
      type="button"
      onClick={openOfficialRules}
      className="font-medium text-[#004C50] underline"
    >
      See Official Rules.
    </button>
  </>
);

// The fine print and the one action, together — they are one block in both
// designs (docked to the bottom of the phone, inside the entrants card on
// desktop) so they are one component rather than two that have to be kept in
// step.
const EnterBlock = ({ game, joining, disabled, onJoin }) => {
  const navigate = useNavigate();
  // The same four states the desktop entry card reads, from the same helper —
  // see `cashEntryState`. It replaces this block's own "Entry closed" label: the
  // client has now said what a nonmember sees once a game is running ("LIVE …
  // CTA: View Leaderboard"), which is an answer where before there was only a
  // dead end.
  const state = cashEntryState(game);
  const onCta = () => {
    if (state.action === "join") return onJoin && onJoin();
    if (state.action === "leaderboard") {
      return navigate(standaloneLeaderboardPath(game?.vanity_url));
    }
    return navigate(`/contest/${game?.vanity_url}`);
  };
  return (
  <>
    <p className="text-center text-[12px] text-secondary">
      <EligibilityNote />
    </p>
    <button
      type="button"
      onClick={onCta}
      disabled={state.action === "join" && (disabled || joining)}
      className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-lg bg-[#004C50] py-4 font-bold text-[15px] text-white shadow-lg transition-colors hover:bg-[#003437] disabled:opacity-60"
    >
      {state.action === "join" && <CashIcon className="h-5 w-5" stroke="#FFFFFF" />}
      {state.action === "join" && joining ? "Entering…" : state.cta}
    </button>
  </>
  );
};

/**
 * `game` is the resolved league. `onJoin`, `joining` and `disabled` come from
 * LeagueJoinPreview, which owns the join request, its failure toast and the
 * celebration for every kind of league — this page renders the button, it does
 * not re-implement joining.
 */
// The mobile CTA is docked over the viewport, `bottom-16` above the app's fixed
// 64px bottom navigation, and the page has to end far enough up that the last
// card scrolls clear of it. `DOCK_GAP` is the white the design leaves between
// the two.
const BOTTOM_NAV_H = 64;
const DOCK_GAP = 16;

// `identityInShell` / `showJoinCta` are the two props every landing card in
// League Play now takes, so one host can render whichever card a competition
// calls for without knowing which it got.
//
//   identityInShell  the host already names the competition above this card —
//                    League details puts it in LeagueShell — so the page drops
//                    its own back link and title banner rather than introducing
//                    the game twice.
//   showJoinCta      false on League details: the reader is already a member,
//                    so the Enter CTA has nothing to offer them. With no dock
//                    there is also nothing at the end of the page to reserve
//                    room for, which is why the dock padding follows it.
const CashGameLandingPage = ({
  game,
  joining = false,
  disabled = false,
  onJoin,
  identityInShell = false,
  showJoinCta = true,
  // League details supplies its own share control in the shell above, so the
  // body's top bar drops its own rather than offering two.
  showShare = true,
}) => {
  // How much room the end of the page owes the docked CTA — measured from the
  // dock rather than written down, because the dock is not a fixed height: the
  // eligibility line above the button wraps to two rows on a narrow phone and
  // takes it from 104px to 122px. A constant sized for a 393px screen buried
  // the Entrants card behind the bar at 320px, which is the same mistake as the
  // gap it was fixing, only harder to see. ResizeObserver so a font swap or a
  // longer label corrects it too.
  const navigate = useNavigate();
  // WHY THE PRIZE CARD WAS NOT STICKY. Client: "The prize card is not sticky."
  //
  // Not a CSS fault on this page — `lg:sticky lg:top-24 lg:self-start` is
  // correct and measures correctly in isolation. Two of the app shell's own
  // elements declare an overflow (.adminLayout, .mainWrapper), which makes THEM
  // the sticky element's scrollport instead of the viewport; that box never
  // scrolls, so the card just travels with the page. index.css clears the clip
  // under `body.league-rail`, and until now only LeagueColumns set that class —
  // which is why a league's Home rail sticks and this page's did not.
  useStickyRail();
  const dockRef = useRef(null);
  const [dockPad, setDockPad] = useState(BOTTOM_NAV_H + 104 + DOCK_GAP);

  useEffect(() => {
    const el = dockRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      // Zero at md, where the dock is display:none and the CTA is in the flow.
      // Nothing to reserve then, and `md:pb-16` is what applies anyway.
      if (h > 0) setDockPad(Math.round(h) + BOTTOM_NAV_H + DOCK_GAP);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
  // Full-bleed on mobile.
  //
  // Every landing page is wrapped in a gutter by its host — LeagueEntry adds
  // `px-4` and the app shell another `px-2` — which is right for the public and
  // private league cards, because those ARE cards and are meant to sit in a
  // margin. This page is not a card: in the design the title banner and the
  // benefit strip run edge to edge and only the content below them is inset.
  // Inheriting 24px of ancestor padding pushed the banner off both edges and
  // then doubled up under the content section's own `px-4`, so the cards sat
  // 40px in where the design puts them at 16px.
  //
  // `margin-inline: calc(50% - 50vw)` widens this element back out to the
  // viewport whatever those ancestors are padded by, rather than `-mx-6`
  // hardcoding today's 8+16 — the shell's padding is site-wide and not ours to
  // depend on. The gutter the design DOES want is then reapplied once, by the
  // content section below. Cancelling it here rather than removing it in the
  // host leaves every other landing page exactly as it is, and desktop keeps
  // its current layout (`md:[margin-inline:0]`) — that is the client's separate
  // desktop file.
  //
  // The bottom spacing is NOT the same story in the other axis any more. This
  // page's CTA is docked over the viewport rather than sitting in the flow, so
  // the end of the document owes it nothing except enough room for the Entrants
  // card to scroll clear of the bar — which is what `--cash-dock-pad` carries,
  // measured from the dock itself.
  //
  // There used to be a `-mb-32` here as well, cancelling 128px of bottom padding
  // the host added for landing pages whose CTA was the last thing in the flow.
  // Every landing page docks its CTA on mobile now, so the host stopped adding
  // it (see LeagueEntry) and the counterweight went with it. Net zero for this
  // page — it was only ever undoing something — and one less pair of numbers
  // that have to stay equal to each other.
  <div
    style={{ "--cash-dock-pad": `${showJoinCta ? dockPad : 0}px` }}
    className="pb-[var(--cash-dock-pad)] [margin-inline:calc(50%_-_50vw)] md:pb-16 md:[margin-inline:0]"
  >
    {/* The two-column desktop design is drawn in a 920px column. BOTH hosts now
        give a cash game exactly that — they share landingContainerClass — so
        872px (920 less the 24px gutters) is the same number the column already
        resolves to and this cap is a belt-and-braces no-op rather than a
        correction. It stays because it is the width the design was drawn at, and
        a third host appearing later should not be able to stretch the page
        without someone noticing. */}
    <div className="md:mx-auto md:max-w-[872px]">
      {/* ── The desktop header row ──────────────────────────────────────
          Client: "Desktop: remove the 'All Contests' text label. Keep the back
          arrow, aligned to the left edge of the content column. Add the share
          icon aligned to the right edge of the content column. The arrow and
          share icon bracket the content area."

          It is the shared LeaguePlayTopBar, so the public and private landings
          get the identical row — "every League Play screen uses one shared
          header… The three screens differ in their body content, never in header
          structure." Mobile takes nothing from here: the app's own fixed bar
          already carries arrow, logo and share, and the client marked that bar
          correct as the template.

          Not rendered inside League details (`identityInShell`), where the shell
          above already owns the screen's chrome. */}
      {!identityInShell && (
        <LeaguePlayTopBar
          game={game}
          showShare={showShare}
          fallback="/contest/cash-leagues"
        />
      )}

      {/* ── TWO COLUMNS, at lg ──────────────────────────────────────────
          Client: "The right-side card becomes sticky and stops at the end of the
          contest rules card."

          Left: header, benefit strip, Contest Rules. Right: the entry card.
          Nothing below the columns belongs to either, so Build your case sits
          outside the row and runs the full width — which is where the client's
          design puts it and where this page already had it.

          Gated at `lg` rather than `md`. The rail is 300px wide in the design;
          at a 768px viewport the container leaves ~400px beside it, which is
          narrower than the phone the left column's contents were drawn for.
          Below lg the two columns simply stack, entry card last. */}
      <div className="lg:flex lg:items-start lg:gap-5">
        <div className="min-w-0 lg:flex-1">
        {/* ── Header ──────────────────────────────────────────────────────
            ONE header at both widths now. It was two — a flat #003B4A ->
            #00242E phone banner and the desktop card — and the client's mobile
            note closed the gap: "Header: Add the CASH GAME eyebrow above the
            game title. Match the hero background to the same dark teal used on
            desktop."

            So the ground, its texture and the eyebrow are shared, and the only
            things that still differ are the ones neither message asked to
            change: the phone runs flush to the screen edges at the type size it
            already shipped, and the HOSTED BY lockup stays desktop-only because
            the phone keeps the separate host-and-countdown row below ("Do not
            remove this row because it is missing from Stitch").

            Client, on the desktop header: "header is the cash game pill, title,
            and tagline; use our signature HOSTED BY circle, Q avatar, and QUSHi
            wordmark logo in white. I like the header color in my Stitch design;
            if you can, match it." */}
        {!identityInShell && (
          <div
            className="relative overflow-hidden px-5 py-5 text-white md:rounded-2xl md:border md:border-[#005B60]/40 md:p-8"
            style={{ background: HEADER_GRADIENT }}
          >
            {/* The design's two surface treatments: a fine dot grid over the
                whole panel and a soft green bloom off the bottom-right corner.
                Both are part of the header the client singled out, both are
                pointer-transparent. */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-screen"
              style={{
                backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
                backgroundSize: "16px 16px",
              }}
            />
            <div className="pointer-events-none absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-[#35B23C]/10 blur-3xl" />

            <div className="relative">
              <CashGamePill />
              <h1 className="mt-2 font-montserrat text-[20px] font-extrabold leading-tight tracking-tight md:mt-3 md:text-3xl">
                {game?.title}
              </h1>
              {game?.tagline && (
                <p className="mt-1 text-[13px] font-medium text-white/80 md:mt-2 md:text-base">
                  {game.tagline}
                </p>
              )}
              {/* Client: "Move HOSTED BY QUSHi into the header like desktop.
                  Remove the circle Q avatar and use only the white QUSHi
                  wordmark." So it is no longer desktop-only, and the separate
                  band that used to carry it below the metabar has gone with it —
                  the countdown that shared that band is now the prize card's
                  top-right status, which is the other half of the same note. */}
              <HostedByQushi size="md" onDark className="mt-4 md:mt-5" />
            </div>
          </div>
        )}

          {/* ── Benefit strip ───────────────────────────────────────────
              The same three facts as the View All metabar, with the design's
              second line under each label. Flush to the phone's edges, and a
              container card from md up — see the `benefits` variant in
              CashGameMetaBar. `md:mt-6` is the gap the design leaves between it
              and the header card above, which the old flush desktop banner did
              not need. */}
          <CashGameMetaBar variant="benefits" className="md:mt-6" />

          {/* The separate HOSTED BY + countdown band that used to sit here is
              gone. Client: "Move HOSTED BY QUSHi into the header like desktop…
              Move Starts in 8h (status line) into the top-right corner of the
              prize card, like the desktop version… get rid of the divider line
              that currently sits under the HOSTED BY QUSHi." Both facts are
              still on the screen, each in one place: the lockup in the header
              above, the countdown in the prize card below. */}

          <div className="px-4 pt-4 md:px-0 md:pt-6">
            {/* The prize and the eligible assets are MOBILE cards now. The
                desktop design states both inside the entry card on the right —
                the prize as its centrepiece and the assets as the first summary
                row — so drawing them here as well would say each thing twice on
                one screen. The phone has no right-hand rail and keeps them. */}
            <div className="space-y-4 md:hidden">
              <PrizeCard game={game} />
              <EligibleAssetsCard game={game} />
            </div>

            <ContestRulesCard game={game} className="mt-4 md:mt-0" />
          </div>
        </div>

        {/* ── The sticky entry card ───────────────────────────────────────
            `lg:sticky lg:top-24 lg:self-start` is the whole of "sticky and stops
            at the end of the contest rules card": the flex row is exactly as
            tall as the left column, which ends with Contest Rules, so the card
            can travel no further than that. Hidden on the phone, where the same
            decision is carried by the prize card and the docked CTA. */}
        {/* Between md and lg the two columns stack and this card is the last
            thing before Build your case. `md:max-w-sm md:mx-auto` is OURS, not
            the client's: their design only describes the lg layout, and a
            prize card stretched to 850px turns the payout box into a pair of
            very wide rules. Capped, it stays the card it is drawn as. */}
        <div className="hidden px-4 pt-4 md:mx-auto md:block md:max-w-sm md:px-0 md:pt-6 lg:mx-0 lg:max-w-none lg:sticky lg:top-24 lg:w-[300px] lg:shrink-0 lg:self-start lg:pt-0">
          <EntryCard
            game={game}
            joining={joining}
            disabled={disabled}
            onJoin={onJoin}
            showCta={showJoinCta}
          />
        </div>
      </div>

      <div className="px-4 md:px-0">
        {/* ── Build your case ─────────────────────────────────────────────
            Full width beneath the columns, which is where the client's design
            puts it and where this page already had it.

            Client: "Same with the badge 'Build Your Case' section: use our
            badges with 1st, 2nd, and 3rd; under them, use the same consistent
            font as the other landing pages. The subtitle is correct on Stitch."
            All three are the shared PodiumRewardsCard as it already stands — the
            real badge artwork with 1st / 2nd / 3rd captions, the type every
            landing page uses, and the Stitch subtitle, which is the sentence
            `subheadingDesktop` already carries. Nothing here changes; the
            client's note is a check that the section is not redrawn from the
            mockup, whose generic star, info and cup glyphs are not our badges.

            `variantKey="cash-game"` is the one thing that differs between the
            three landing pages: a cash game is a public league by `game_type`,
            so it used to borrow the public league badges; it has its own gold
            artwork now. */}
        <PodiumRewardsCard
          className="mt-4 md:mt-6"
          variantKey="cash-game"
          surface="landing"
          subheadingDesktop="A top 3 finish adds a badge to your trophy case."
          tinted
        />

        {/* ── The eligibility line, on the PREVIEW ────────────────────────
            Client: "CASH GAMES PREVIEW ONLY — the landing page preview cards for
            cash games no longer have the sticky Enter Free CTA, which is
            correct, but let the 'Available to eligible U.S. residents. See
            Official Rules' sit inline at the bottom — not sticky."

            The note used to travel with the CTA, so dropping the CTA dropped a
            legal line that is true whether or not anyone can still enter. Here
            it is its own last line of the page, and only when there is no CTA —
            with one, the note sits above the button where it always has. */}
        {!showJoinCta && (
          <p className="mt-6 text-center text-[12px] text-secondary">
            <EligibilityNote />
          </p>
        )}
      </div>
    </div>

    {/* MOBILE sticky CTA. `bottom-16` clears the fixed 64px bottom nav rather
        than sitting on top of it; the page's own bottom padding, measured from
        this element, clears both. */}
    {showJoinCta && (
      <div
        ref={dockRef}
        className="fixed inset-x-0 bottom-16 z-40 border-t border-gray-200 bg-white/95 px-4 pb-3 pt-2 backdrop-blur-md md:hidden"
      >
        <div className="mx-auto max-w-md">
          <EnterBlock
            game={game}
            joining={joining}
            disabled={disabled}
            onJoin={onJoin}
          />
        </div>
      </div>
    )}
  </div>
  );
};

export default CashGameLandingPage;
